import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HyperliquidNav from '../../components/trading/HyperliquidNav';
import AccountsCarousel from './components/AccountsCarousel';
import { ACCOUNT_PACKAGES } from './components/AccountPackages';
import { useAccessLevel } from '../../../hooks/useAccessLevel';
import { useWallet } from '../../../context/WalletContext';
import './Accounts.css';

const Accounts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isConnected, address } = useWallet();
  const { isLoading, checkCanPurchaseLP } = useAccessLevel();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [lpCheckResult, setLpCheckResult] = useState(null);
  const [isCheckingLP, setIsCheckingLP] = useState(false);

  // Verificar si puede comprar LP cuando se carga la página
  useEffect(() => {
    const verifyLPPurchase = async () => {
      if (!isConnected || !address) {
        return;
      }

      setIsCheckingLP(true);
      try {
        const result = await checkCanPurchaseLP();
        setLpCheckResult(result);
      } catch (error) {
        console.error('[Accounts] Error verificando compra LP:', error);
        setLpCheckResult({
          canPurchaseLP: false,
          hasNFT: false,
          message: 'Error verifying LP purchase eligibility.',
        });
      } finally {
        setIsCheckingLP(false);
      }
    };

    verifyLPPurchase();
  }, [isConnected, address, checkCanPurchaseLP]);

  const handleSubscribe = (pkg) => {
    // Si no puede comprar LP, no permitir suscripción
    if (lpCheckResult && !lpCheckResult.canPurchaseLP) {
      return;
    }
    if (!pkg.isActive) {
      return;
    }
    setSelectedPackage(pkg);
    localStorage.setItem('selected_account_package', JSON.stringify(pkg));
    navigate('/accounts/confirmation');
  };

  const handleBack = () => {
    navigate('/trading');
  };

  const handlePurchaseNFT = () => {
    navigate('/nft/select-nft-collection');
  };

  // Mostrar bloqueo si no puede comprar LP
  const showBlocked = 
    !isLoading && 
    !isCheckingLP && 
    lpCheckResult && 
    !lpCheckResult.canPurchaseLP;

  return (
    <div className="accounts-page">
      <HyperliquidNav />
      
      <div className="accounts-container">
        <button onClick={handleBack} className="back-btn">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{t('accounts.back')}</span>
        </button>

        {isLoading || isCheckingLP ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            fontSize: '16px',
            color: '#666'
          }}>
            {t('common.loading', 'Loading...')}
          </div>
        ) : showBlocked ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '40px 20px',
            textAlign: 'center',
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px',
              }}>
                {t('accounts.nft_required_title', 'NFT Required')}
              </div>
              <div style={{
                fontSize: '16px',
                color: '#ffffff',
                opacity: 0.9,
                marginBottom: '32px',
                lineHeight: '1.6',
              }}>
                {lpCheckResult?.message || 
                  t('accounts.nft_required_message', 
                    'You must purchase a Premium NFT ($30) before you can purchase LP trading accounts.')
                }
              </div>
              <button
                onClick={handlePurchaseNFT}
                style={{
                  background: '#ffffff',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '14px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0f0f0';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
              >
                {t('accounts.purchase_nft_button', 'Purchase NFT')} - $30
              </button>
            </div>
          </div>
        ) : (
          <div className="accounts-content">
            <AccountsCarousel
              packages={ACCOUNT_PACKAGES}
              selectedPackage={selectedPackage}
              onSelectPackage={handleSubscribe}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Accounts;

